import { FC, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { Modal } from 'src/components/modal'
import { Button } from 'src/components/button'
import { Input } from 'src/components/input'
import { ErrorsList } from 'src/components/errors'
import { propertyApi } from 'src/services/properties/api'
import { propertyQueries } from 'src/services/properties/queries'
import {
  DuplicatePropertyForm,
  DuplicatePropertyFormInputs,
  TDuplicatePropertyForm,
} from 'src/services/properties/forms'

interface DuplicatePropertyModalProps {
  isOpen: boolean
  onClose: () => void
  /** Property being copied. */
  propertyId: string
}

const DuplicatePropertyModalInner: FC<DuplicatePropertyModalProps> = ({
  isOpen,
  onClose,
  propertyId,
}) => {
  const { t } = useTranslation()
  const { form, createFormFieldChangeHandler, validate } = useTnForm<TDuplicatePropertyForm>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { mutate: duplicate, isPending } = useMutation({
    mutationFn: (name: string) => propertyApi.csc.duplicate({ id: propertyId, name }),
    onSuccess: (copy) => {
      queryClient.invalidateQueries({ queryKey: propertyQueries.all() })
      toast.success(t('propertyForm.duplicatedToast'))
      onClose()
      navigate(`/properties/${copy.id}`)
    },
    onError: () => toast.error(t('propertyForm.duplicateError')),
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    validate()
    if (!form.isValid) return
    duplicate((form._name.value ?? '').trim())
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('propertyForm.duplicateTitle')} size="md">
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="duplicate-property-form">
        <div>
          <Input
            autoFocus
            label={t('propertyForm.duplicateNameLabel')}
            hint={t('propertyForm.duplicateHint')}
            placeholder={form._name.placeholder}
            value={form._name.value ?? ''}
            onChange={(e) => createFormFieldChangeHandler(form._name)(e.target.value)}
            data-testid="duplicate-property-name"
          />
          <ErrorsList errors={form._name.errors} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={isPending} data-testid="duplicate-property-submit">
            {isPending ? t('propertyForm.duplicatingBtn') : t('propertyForm.duplicateSubmit')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export const DuplicatePropertyModal: FC<DuplicatePropertyModalProps> = (props) => (
  // Re-mount the form when the modal toggles so the name resets between openings.
  <FormProvider<DuplicatePropertyFormInputs>
    formClass={DuplicatePropertyForm}
    key={props.isOpen ? 'open' : 'closed'}
  >
    <DuplicatePropertyModalInner {...props} />
  </FormProvider>
)
